import { fmtCompactEur, fmtCompactUsers, fmtEur2 } from '../formatters.js';
import { MODEL_ASSUMPTIONS, MODEL_CHECKPOINTS } from '../modelSnapshot.js';

const { m0, m6, m12 } = MODEL_CHECKPOINTS;

const SCENARIOS = [
  {
    key: 'm0',
    label: 'M0 baseline',
    headline: fmtCompactEur(m0.totalArr),
    barPct: m0.totalArr / m12.totalArr,
    metrics: [
      ['B2C cardholders', fmtCompactUsers(m0.cardholders)],
      ['B2C ARR', fmtCompactEur(m0.b2cArr)],
      ['B2B ARR', fmtCompactEur(m0.b2bArr)],
      ['Total ARR', fmtCompactEur(m0.totalArr)],
      ['B2C rev / cardholder', '$0.00'],
      ['Model state', 'B2B only'],
    ],
    note: 'The plan starts with the existing B2B run-rate and no consumer cardholder revenue.',
  },
  {
    key: 'm6',
    label: 'M6 checkpoint',
    headline: fmtCompactEur(m6.totalArr),
    barPct: m6.totalArr / m12.totalArr,
    metrics: [
      ['B2C cardholders', fmtCompactUsers(m6.cardholders)],
      ['B2C ARR', fmtCompactEur(m6.b2cArr)],
      ['B2B ARR', fmtCompactEur(m6.b2bArr)],
      ['Total ARR', fmtCompactEur(m6.totalArr)],
      ['B2C rev / cardholder', '$3.81'],
      ['Model state', 'B2C live'],
    ],
    note: 'By Month 6, the model needs 30K B2C cardholders and $2.50M total ARR / run-rate.',
  },
  {
    key: 'm12',
    label: 'M12 target',
    headline: fmtCompactEur(m12.totalArr),
    barPct: 1,
    metrics: [
      ['B2C cardholders', fmtCompactUsers(m12.cardholders)],
      ['B2C ARR', fmtCompactEur(m12.b2cArr)],
      ['B2B ARR', fmtCompactEur(m12.b2bArr)],
      ['Total ARR', fmtCompactEur(m12.totalArr)],
      ['B2C rev / cardholder', fmtEur2(MODEL_ASSUMPTIONS.month12RevenuePerCardholder)],
      ['Known costs', `${fmtCompactEur(MODEL_ASSUMPTIONS.month12KnownMonthlyCosts)} / mo`],
    ],
    note: 'Month 12 target: $6.26M total ARR, with B2C now the majority of revenue.',
  },
];

const STREAMS = [
  ['B2C fee streams', 6, 'Trading, subscriptions, FX/remittance, credit, interchange, and yield'],
  ['B2B ARR stream', 1, 'Existing B2B ARR scaling from $750K to $1.5M'],
];

const MILESTONES = [
  ['B2C cardholders', fmtCompactUsers(m12.cardholders), 'Month 12 target'],
  ['B2C ARR', fmtCompactEur(m12.b2cArr), 'Annualized M12 B2C run-rate'],
  ['B2B ARR', fmtCompactEur(m12.b2bArr), 'Month 12 B2B run-rate'],
  ['Total ARR', fmtCompactEur(m12.totalArr), 'Month 12 target'],
];

const FUNNEL = [
  {
    label: 'M0 cardholders',
    value: fmtCompactUsers(m0.cardholders),
    sub: 'B2C not launched',
    pct: 0,
  },
  {
    label: 'M6 cardholders',
    value: fmtCompactUsers(m6.cardholders),
    sub: 'Midpoint checkpoint',
    pct: m6.cardholders / m12.cardholders,
  },
  {
    label: 'M12 cardholders',
    value: fmtCompactUsers(m12.cardholders),
    sub: 'Base-case target',
    pct: 1,
  },
  {
    label: 'M12 B2C rev / cardholder',
    value: '$3.96',
    sub: 'Monthly blended revenue',
    pct: MODEL_ASSUMPTIONS.month12RevenuePerCardholder / 4,
  },
];

const REVENUE_MIX = [
  {
    label: 'B2C ARR',
    value: fmtCompactEur(m12.b2cArr),
    pct: m12.b2cArr / m12.totalArr,
    color: '#1F1F1F',
  },
  {
    label: 'B2B ARR',
    value: fmtCompactEur(m12.b2bArr),
    pct: m12.b2bArr / m12.totalArr,
    color: '#5C97C2',
  },
];

const USE_OF_FUNDS = [
  { label: 'Growth + Money Back', value: '$1.8M', pct: 0.45 },
  { label: 'Card, wallet, liquidity ops', value: '$0.9M', pct: 0.225 },
  { label: 'Partner integrations', value: '$0.6M', pct: 0.15 },
  { label: 'Compliance, opex, runway', value: '$0.7M', pct: 0.175 },
];

const TIMELINE = [
  ['M0', 'B2B base', '$750K ARR run-rate'],
  ['M2', 'B2C starts', '6K cardholders and $1.13M total ARR'],
  ['M6', 'Midpoint', '30K cardholders and $2.50M total ARR'],
  ['M12', 'Target', '100K cardholders and $6.26M total ARR'],
];

export default function AdvancedScenarioGraphics() {
  return (
    <>
      <section className="rounded-card border border-carbon-100 bg-white p-7">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div>
            <p className="caption text-carbon-400">Forward scenario map</p>
            <h2 className="h3 mt-2">The model now anchors on M0, M6, and M12.</h2>
          </div>
          <p className="label text-carbon-500 max-w-md">
            The latest sheet is a 12-month revenue plan: existing B2B ARR first, then B2C cardholder
            ramp.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {SCENARIOS.map((scenario) => (
            <ScenarioCard key={scenario.key} scenario={scenario} />
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[1.08fr_0.92fr]">
        <ConversionFunnel />
        <RevenueMixChart />
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
        <UseOfFundsChart />
        <MilestoneTimeline />
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
        <section className="rounded-card border border-carbon-100 bg-white p-7">
          <p className="caption text-carbon-400">Revenue stream picture</p>
          <h2 className="h3 mt-2">Six B2C streams plus one B2B ARR line.</h2>
          <div className="mt-6 space-y-5">
            {STREAMS.map(([label, count, description]) => (
              <div key={label}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="label text-carbon-700">{label}</p>
                  <p className="text-2xl font-semibold num-tabular">{count}</p>
                </div>
                <div className="mt-2 h-3 rounded-full overflow-hidden bg-carbon-50">
                  <div
                    className={
                      label === 'B2C fee streams' ? 'h-full bg-carbon-800' : 'h-full bg-ice-500'
                    }
                    style={{ width: `${(count / 7) * 100}%` }}
                  />
                </div>
                <p className="body-md text-carbon-500 mt-3">{description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-card border border-carbon-100 bg-white p-7">
          <p className="caption text-carbon-400">Milestone stack</p>
          <h2 className="h3 mt-2">What investors should be watching.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {MILESTONES.map(([label, value, sub]) => (
              <div key={label} className="rounded-box border border-carbon-100 bg-carbon-50 p-4">
                <p className="caption text-carbon-400">{label}</p>
                <p className="text-2xl font-semibold tracking-h2 num-tabular mt-2">{value}</p>
                <p className="label text-carbon-500 mt-2">{sub}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function ConversionFunnel() {
  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <p className="caption text-carbon-400">Conversion funnel</p>
      <h2 className="h3 mt-2">From B2C launch to revenue-bearing usage.</h2>
      <div className="mt-7 space-y-4">
        {FUNNEL.map((step) => (
          <div key={step.label}>
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <p className="label text-carbon-800">{step.label}</p>
                <p className="caption text-carbon-400 mt-1">{step.sub}</p>
              </div>
              <p className="text-2xl font-semibold num-tabular">{step.value}</p>
            </div>
            <div className="mt-2 h-5 rounded-sw overflow-hidden bg-carbon-50">
              <div className="h-full bg-carbon-800" style={{ width: `${step.pct * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RevenueMixChart() {
  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <p className="caption text-carbon-400">Revenue target mix</p>
      <h2 className="h3 mt-2">$6.26M target-case composition.</h2>
      <div className="mt-7 h-5 rounded-full overflow-hidden flex bg-carbon-50">
        {REVENUE_MIX.map((segment) => (
          <div
            key={segment.label}
            className="h-full"
            style={{ width: `${segment.pct * 100}%`, background: segment.color }}
            title={`${segment.label}: ${segment.value}`}
          />
        ))}
      </div>
      <div className="mt-6 space-y-4">
        {REVENUE_MIX.map((segment) => (
          <div key={segment.label} className="flex items-center justify-between gap-4">
            <div>
              <p className="label text-carbon-800">{segment.label}</p>
              <p className="caption text-carbon-400 mt-1">{Math.round(segment.pct * 100)}% mix</p>
            </div>
            <p className="text-xl font-semibold num-tabular">{segment.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function UseOfFundsChart() {
  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <p className="caption text-carbon-400">Use of funds</p>
          <h2 className="h3 mt-2">$4M allocation picture.</h2>
        </div>
        <p className="text-2xl font-semibold num-tabular">$4M</p>
      </div>
      <div className="mt-6 space-y-4">
        {USE_OF_FUNDS.map((row) => (
          <div key={row.label}>
            <div className="flex items-baseline justify-between gap-4">
              <p className="label text-carbon-800">{row.label}</p>
              <p className="label text-carbon-500 num-tabular">
                {row.value} · {Math.round(row.pct * 100)}%
              </p>
            </div>
            <div className="mt-2 h-3 rounded-full overflow-hidden bg-carbon-50">
              <div className="h-full bg-ice-500" style={{ width: `${row.pct * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MilestoneTimeline() {
  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <p className="caption text-carbon-400">Milestone timeline</p>
      <h2 className="h3 mt-2">The investor proof path.</h2>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-3">
        {TIMELINE.map(([quarter, title, body]) => (
          <div key={quarter} className="rounded-box border border-carbon-100 bg-carbon-50 p-4">
            <p className="caption text-ice-700">{quarter}</p>
            <h3 className="text-base font-semibold mt-3 leading-snug">{title}</h3>
            <p className="label text-carbon-500 mt-3">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ScenarioCard({ scenario }) {
  return (
    <div className="rounded-card border border-carbon-100 bg-carbon-50 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="caption text-ice-700">{scenario.label}</p>
          <p className="text-4xl font-semibold tracking-h2 num-tabular mt-3">{scenario.headline}</p>
        </div>
      </div>

      <div className="mt-5 h-2.5 rounded-full overflow-hidden bg-white">
        <div className="h-full bg-ice-500" style={{ width: `${scenario.barPct * 100}%` }} />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5">
        {scenario.metrics.map(([label, value]) => (
          <MiniMetric key={label} label={label} value={value} />
        ))}
      </div>

      <p className="body-md text-carbon-500 mt-5">{scenario.note}</p>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div>
      <p className="caption text-carbon-400">{label}</p>
      <p className="label text-carbon-800 num-tabular mt-1">{value}</p>
    </div>
  );
}
